import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

/**
 * Reads a px value from an element's computed style, refreshing on parent
 * inline-style mutations — so it picks up changes to CSS variables that the
 * element inherits or whose value its own styles depend on (0 if unset or
 * unparseable).
 */
export const useComputedStylePx = (
  ref: RefObject<HTMLElement>,
  property: string,
): number => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const read = () => {
      const px =
        parseFloat(getComputedStyle(element).getPropertyValue(property)) || 0;
      setValue(prev => (prev === px ? prev : px));
    };

    read();

    const parent = element.parentElement;
    if (!parent) return;

    const observer = new MutationObserver(read);
    observer.observe(parent, { attributeFilter: ['style'], attributes: true });

    return () => observer.disconnect();
  }, [property, ref]);

  return value;
};

export default useComputedStylePx;
